"use client"

import { useState, useEffect } from "react"
import { motion } from "framer-motion"
import { Users, BookOpen, Plus, ShieldCheck, Database, LogOut, Loader2, Trash2, ImagePlus, UploadCloud, CheckCircle2, Edit2, LayoutGrid, HelpCircle, MessageSquare, FileText, Menu, X, Award, Eye, EyeOff, Edit, Image as ImageIcon, RefreshCw } from "lucide-react"
import { collection, addDoc, getDocs, deleteDoc, updateDoc, doc, serverTimestamp, query, orderBy, setDoc, getDoc } from "firebase/firestore"
import { ref, uploadBytes, getDownloadURL } from "firebase/storage"
import { auth, db, storage } from "@/lib/firebase"
import { signOut, onAuthStateChanged } from "firebase/auth"
import { toast } from "sonner"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import FAQManager from "./faq-manager"
import ReviewsManager from "./reviews-manager"
import QuizzesManager from "./quizzes-manager"
import PYQManager from "./pyq-manager"
import ArticlesManager from "./articles-manager"
import MagazinesManager from "./magazines-manager"
import TestimonialsManager from "./testimonials-manager"
import StaffManager from "./staff-manager"
import UsersManager from "./users-manager"
import AdminOverview from "./admin-overview"
import MediaLibrary from "./media-library"
import MaintenanceManager from "./maintenance-manager"

export default function AdminDashboard() {
  const [activeTab, setActiveTab] = useState("overview")
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [courses, setCourses] = useState<any[]>([])

  useEffect(() => {
    const fetchCourses = async () => {
      const snap = await getDocs(collection(db, "courses"))
      setCourses(snap.docs.map(d => ({ id: d.id, ...d.data() })))
    }
    fetchCourses()
  }, [])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [coursesList, setCoursesList] = useState<any[]>([])
  const [isLoadingCourses, setIsLoadingCourses] = useState(true)

  // Edit State
  const [editingCourseId, setEditingCourseId] = useState<string | null>(null)

  // Advanced Image Upload State
  const [imageFile, setImageFile] = useState<File | null>(null)
  const [imagePreview, setImagePreview] = useState<string | null>(null)
  const [isMediaLibraryOpen, setIsMediaLibraryOpen] = useState(false)

  // Course Form State
  const [formData, setFormData] = useState({
    title: "", instructor: "", duration: "", portal: "tspsc", type: "paid", originalPrice: "", discountedPrice: "", tag: "", description: ""
  })

  // Safe reset function
  const resetFormState = () => {
    setFormData({ title: "", instructor: "", duration: "", portal: "tspsc", type: "paid", originalPrice: "", discountedPrice: "", tag: "", description: "" })
    setImageFile(null)
    setImagePreview(null)
    setEditingCourseId(null)
  }

  const fetchLiveCourses = async () => {
    setIsLoadingCourses(true)
    try {
      const q = query(collection(db, "courses"), orderBy("createdAt", "desc"))
      const querySnapshot = await getDocs(q)
      const data = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
      setCoursesList(data)
    } catch (error) {
      console.error(error)
    } finally {
      setIsLoadingCourses(false)
    }
  }

  // ✅ Auto-register: write current user's UID into /admins so Firestore rules recognize them
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) return
      try {
        const adminRef = doc(db, "admins", user.uid)
        const adminSnap = await getDoc(adminRef)
        if (!adminSnap.exists()) {
          await setDoc(adminRef, {
            uid: user.uid,
            email: user.email,
            registeredAt: serverTimestamp()
          })
          console.log("✅ Admin registered in /admins:", user.uid)
        }
      } catch (e) {
        console.warn("⚠️ Could not auto-register admin:", e)
      }
    })
    return () => unsub()
  }, [])

  useEffect(() => {
    if (activeTab === "overview") {
      fetchLiveCourses()
      resetFormState() // Clear form if moving away
    }
  }, [activeTab])

  const handleLogout = async () => {
    await signOut(auth)
  }

  const handleDeleteCourse = async (id: string) => {
    if (!window.confirm("Are you sure you want to permanently delete this masterclass?")) return
    if (!auth.currentUser) return toast.error("Session expired.")
    try {
      await deleteDoc(doc(db, "courses", id))
      toast.success("Course purged from Database")
      fetchLiveCourses()
    } catch (error) {
       toast.error("Failed to delete course.")
    }
  }

  const handleEditCourse = (course: any) => {
     // Prepare form state
     setFormData({
       title: course.title || "",
       instructor: course.instructor || "",
       duration: course.duration || "",
       portal: course.portal || "tspsc",
       type: course.type || "paid",
       originalPrice: course.originalPrice ? String(course.originalPrice) : "",
       discountedPrice: course.discountedPrice ? String(course.discountedPrice) : "",
       tag: course.tag || "",
       description: course.description || ""
     })
     
     setImagePreview(course.image || null)
     setImageFile(null) 

     setEditingCourseId(course.id)
     setActiveTab("add-course")
     window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const toggleCourseVisibility = async (course: any) => {
    try {
      const newStatus = course.isVisible === false ? true : false
      await updateDoc(doc(db, "courses", course.id), { isVisible: newStatus })
      toast.success(newStatus ? "Course visible" : "Course hidden")
      setCourses(prev => prev.map(c => c.id === course.id ? { ...c, isVisible: newStatus } : c))
      // Update the local list as well
      setCoursesList(prev => prev.map(c => c.id === course.id ? { ...c, isVisible: newStatus } : c))
    } catch (error) {
      toast.error("Visibility update failed")
    }
  }

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0]
      setImageFile(file)
      setImagePreview(URL.createObjectURL(file))
    }
  }

  const handleCourseSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!auth.currentUser) return toast.error("Unauthorized.")
    setIsSubmitting(true)

    try {
      const originalPriceNum = formData.type === "free" ? 0 : Number(formData.originalPrice)
      const discountedPriceNum = formData.type === "free" ? 0 : Number(formData.discountedPrice)

      let finalImageUrl = imagePreview || ""

      // Upload new image if they selected one
      if (imageFile) {
         toast.info("Uploading high-resolution thumbnail...")
         const storageRef = ref(storage, `course_thumbnails/${Date.now()}_${imageFile.name}`)
         await uploadBytes(storageRef, imageFile)
         finalImageUrl = await getDownloadURL(storageRef)
         
         await addDoc(collection(db, "media"), {
            title: formData.title + " Cover",
            url: finalImageUrl,
            type: "image",
            folder: "images",
            createdAt: serverTimestamp(),
         })
      } else if (!imagePreview && !imageFile && editingCourseId) {
         finalImageUrl = ""
      }

      const slug = formData.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)+/g, '')

      const payload = {
        title: formData.title,
        slug,
        instructor: formData.instructor,
        duration: formData.duration,
        portal: formData.portal,
        type: formData.type,
        originalPrice: originalPriceNum,
        discountedPrice: discountedPriceNum,
        fees: discountedPriceNum, // Alias for unified access
        description: formData.description || "Elite mentorship for administrative and civil service roles.",
        tag: formData.tag,
        image: finalImageUrl,
        isVisible: true
      }

      if (editingCourseId) {
         toast.info("Updating module in database...")
         // Try to preserve existing visibility
         const existing = courses.find(c => c.id === editingCourseId)
         await updateDoc(doc(db, "courses", editingCourseId), {
            ...payload,
            isVisible: existing?.isVisible !== undefined ? existing.isVisible : true
         })
         toast.success("Course Updated Successfully!")
      } else {
         toast.info("Registering module in database...")
         await addDoc(collection(db, "courses"), {
            ...payload,
            createdAt: serverTimestamp(),
            scholars: "0"
         })
         toast.success("Course Published Successfully!")
      }
      
      resetFormState()
      setActiveTab("overview")

    } catch (error) {
      console.error(error)
      toast.error("Failed to process course. Verify Storage Rules.")
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col pb-24 overflow-x-hidden">
      
      {/* Heavy Architectural Dark Header for Contrast */}
      <div className="bg-slate-950 border-b border-slate-900 pb-24 sm:pb-32 pt-12 sm:pt-24 px-4 sm:px-8 shadow-xl relative z-0">
         <div className="max-w-[1400px] mx-auto flex flex-col sm:flex-row justify-between items-center sm:items-end gap-8 relative z-10 w-full group">
            {/* Bespoke Header Identity */}
            <div className="flex flex-col sm:flex-row items-center sm:items-end gap-6 sm:gap-10 text-center sm:text-left w-full sm:w-auto overflow-hidden">
               <div className="relative shrink-0">
                  {/* Infinite Slow Subtle Rotation with Pauses */}
                  <motion.div 
                    animate={{ rotate: [0, 90, 90, 180, 180, 270, 270, 360, 360] }}
                    transition={{ 
                      duration: 20, 
                      repeat: Infinity, 
                      ease: "easeInOut",
                      times: [0, 0.1, 0.25, 0.35, 0.5, 0.6, 0.75, 0.85, 1]
                    }}
                    className="w-32 h-32 sm:w-48 sm:h-48 flex items-center justify-center shadow-[0_10px_50px_rgba(180,140,54,0.15)] relative z-20 border border-[#b48c36]/10 overflow-hidden"
                  >
                     <img src="/logo-final.png" alt="Rohith's Academy" className="w-full h-full object-cover" />
                     <div className="absolute top-0 left-0 w-2 h-2 border-t border-l border-[#b48c36]" />
                     <div className="absolute bottom-0 right-0 w-2 h-2 border-b border-r border-[#b48c36]" />
                  </motion.div>
               </div>
               <div className="space-y-4 overflow-hidden">
                  <div className="flex items-center justify-center sm:justify-start gap-3">
                     <div className="w-2 h-2 rounded-full bg-[#b48c36]" />
                     <p className="text-[#b48c36] font-bold text-[10px] sm:text-[11px] uppercase tracking-[0.3em]">System Live</p>
                  </div>
                  <h1 className="text-3xl sm:text-5xl lg:text-6xl font-serif font-bold text-white tracking-tight leading-none truncate">
                    Admin <br className="hidden sm:block" /> <span className="text-[#b48c36]">Dashboard.</span>
                  </h1>
               </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
               <button onClick={() => setIsMenuOpen(true)} className="xl:hidden flex items-center justify-center gap-3 text-[10px] font-black uppercase tracking-[0.3em] text-white border border-white/20 px-8 py-4 bg-white/5 backdrop-blur-md">
                  <Menu size={16} /> Navigation
               </button>
               <button onClick={handleLogout} className="flex items-center justify-center gap-3 text-[10px] font-black uppercase tracking-[0.3em] text-[#b48c36] border-[0.5px] border-[#b48c36]/20 px-8 py-4 hover:bg-[#b48c36] hover:text-white transition-all duration-700 bg-white/5 backdrop-blur-md">
                  <LogOut size={16} /> Logout
               </button>
            </div>
         </div>
      </div>

      {/* Mobile Navigation Drawer */}
      {isMenuOpen && (
         <motion.div 
           initial={{ opacity: 0 }}
           animate={{ opacity: 1 }}
           className="fixed inset-0 bg-slate-950/90 backdrop-blur-md z-[100] xl:hidden"
         >
            <div className="flex flex-col h-full p-8">
               <div className="flex justify-between items-center mb-12">
                  <span className="text-[#b48c36] font-serif font-black tracking-widest">MENU</span>
                  <button onClick={() => setIsMenuOpen(false)} className="text-white p-2">
                     <X size={32} />
                  </button>
               </div>
               
               <nav className="flex flex-col gap-6 overflow-y-auto no-scrollbar">
                  {[
                     { id: 'overview', label: 'Overview', icon: LayoutGrid },
                     { id: 'add-course', label: 'Publish Module (Courses)', icon: Plus },
                     { id: 'faqs', label: 'FAQ Moderation', icon: HelpCircle },
                     { id: 'reviews', label: 'Student Reviews', icon: MessageSquare },
                     { id: 'quizzes', label: 'Quiz', icon: BookOpen },
                     { id: 'articles', label: 'Current Affairs', icon: FileText },
                     { id: 'pyqs', label: 'PYQ', icon: Database },
                     { id: 'magazines', label: 'Magazines', icon: BookOpen },
                     { id: 'staff', label: 'Staff', icon: Users },
                     { id: 'users', label: 'Users Directory', icon: LayoutGrid },
                     { id: 'testimonials', label: 'Testimonials', icon: Award },
                     { id: 'media', label: 'Media Library', icon: ImageIcon },
                     { id: 'maintenance', label: 'Maintenance', icon: RefreshCw },
                  ].map((tab) => (
                     <button 
                        key={tab.id}
                        onClick={() => { setActiveTab(tab.id as any); setIsMenuOpen(false); }}
                        className={`flex items-center gap-6 py-4 border-b border-white/5 text-left transition-colors ${activeTab === tab.id ? 'text-[#b48c36]' : 'text-white/60'}`}
                     >
                        {tab.icon && <tab.icon size={24} />}
                        <span className="text-2xl font-serif font-bold">{tab.label}</span>
                     </button>
                  ))}
               </nav>
            </div>
         </motion.div>
      )}

      {/* Main Container pulling up over the dark header (Chiaroscuro) */}
      <div className="max-w-[1400px] mx-auto px-4 sm:px-8 w-full -mt-20 sm:-mt-24 relative z-10 grid grid-cols-1 xl:grid-cols-12 gap-6 lg:gap-12 pb-20">
         
         {/* Prestige White Sidebar Ledger - Desktop Only Vertical */}
         <div className="hidden xl:flex xl:col-span-3 flex-col gap-2 bg-white p-4 shadow-[0_2px_20px_-4px_rgba(0,0,0,0.05)] border border-slate-200 self-start">
            <button onClick={() => setActiveTab('overview')} className={`flex items-center gap-4 px-6 py-4 text-left transition-all ${activeTab === 'overview' ? 'bg-slate-50 border-l-2 border-[#b48c36] text-[#b48c36]' : 'border-l-2 border-transparent text-slate-500 hover:text-slate-900 hover:bg-slate-50'}`}>
               <LayoutGrid size={16} className="shrink-0" />
               <span className="font-serif text-lg font-bold">Database Overview</span>
            </button>

            <button onClick={() => setActiveTab('add-course')} className={`flex items-center gap-4 px-6 py-4 text-left transition-all ${activeTab === 'add-course' ? 'bg-slate-50 border-l-2 border-[#b48c36] text-[#b48c36]' : 'border-l-2 border-transparent text-slate-500 hover:text-slate-900 hover:bg-slate-50'}`}>
               <Plus size={16} className="shrink-0" />
               <span className="font-serif text-lg font-bold">Publish Module (Courses)</span>
            </button>

            <button onClick={() => setActiveTab('faqs')} className={`flex items-center gap-4 px-6 py-4 text-left transition-all ${activeTab === 'faqs' ? 'bg-slate-50 border-l-2 border-[#b48c36] text-[#b48c36]' : 'border-l-2 border-transparent text-slate-500 hover:text-slate-900 hover:bg-slate-50'}`}>
               <HelpCircle size={16} className="shrink-0" />
               <span className="font-serif text-lg font-bold">FAQ Moderation</span>
            </button>

            <button onClick={() => setActiveTab('reviews')} className={`flex items-center gap-4 px-6 py-4 text-left transition-all ${activeTab === 'reviews' ? 'bg-slate-50 border-l-2 border-[#b48c36] text-[#b48c36]' : 'border-l-2 border-transparent text-slate-500 hover:text-slate-900 hover:bg-slate-50'}`}>
               <MessageSquare size={16} className="shrink-0" />
               <span className="font-serif text-lg font-bold">Student Reviews</span>
            </button>

            <button onClick={() => setActiveTab('quizzes')} className={`flex items-center gap-4 px-6 py-4 text-left transition-all ${activeTab === 'quizzes' ? 'bg-slate-50 border-l-2 border-[#b48c36] text-[#b48c36]' : 'border-l-2 border-transparent text-slate-500 hover:text-slate-900 hover:bg-slate-50'}`}>
               <BookOpen size={16} className="shrink-0" />
               <span className="font-serif text-lg font-bold">Quiz</span>
            </button>

            <button onClick={() => setActiveTab('pyqs')} className={`flex items-center gap-4 px-6 py-4 text-left transition-all ${activeTab === 'pyqs' ? 'bg-slate-50 border-l-2 border-[#b48c36] text-[#b48c36]' : 'border-l-2 border-transparent text-slate-500 hover:text-slate-900 hover:bg-slate-50'}`}>
               <Database size={16} className="shrink-0" />
               <span className="font-serif text-lg font-bold">PYQ</span>
            </button>

            <button onClick={() => setActiveTab('articles')} className={`flex items-center gap-4 px-6 py-4 text-left transition-all ${activeTab === 'articles' ? 'bg-slate-50 border-l-2 border-[#b48c36] text-[#b48c36]' : 'border-l-2 border-transparent text-slate-500 hover:text-slate-900 hover:bg-slate-50'}`}>
               <FileText size={16} className="shrink-0" />
               <span className="font-serif text-lg font-bold">Current Affairs</span>
            </button>

            <button onClick={() => setActiveTab('magazines')} className={`flex items-center gap-4 px-6 py-4 text-left transition-all ${activeTab === 'magazines' ? 'bg-slate-50 border-l-2 border-[#b48c36] text-[#b48c36]' : 'border-l-2 border-transparent text-slate-500 hover:text-slate-900 hover:bg-slate-50'}`}>
               <BookOpen size={16} className="shrink-0" />
               <span className="font-serif text-lg font-bold">Magazines</span>
            </button>

            <button onClick={() => setActiveTab('staff')} className={`flex items-center gap-4 px-6 py-4 text-left transition-all ${activeTab === 'staff' ? 'bg-slate-50 border-l-2 border-[#b48c36] text-[#b48c36]' : 'border-l-2 border-transparent text-slate-500 hover:text-slate-900 hover:bg-slate-50'}`}>
               <Users size={16} className="shrink-0" />
               <span className="font-serif text-lg font-bold">Staff</span>
            </button>

            <button onClick={() => setActiveTab('users')} className={`flex items-center gap-4 px-6 py-4 text-left transition-all ${activeTab === 'users' ? 'bg-slate-50 border-l-2 border-[#b48c36] text-[#b48c36]' : 'border-l-2 border-transparent text-slate-500 hover:text-slate-900 hover:bg-slate-50'}`}>
               <Users size={16} className="shrink-0" />
               <span className="font-serif text-lg font-bold">Users Directory</span>
            </button>
            
            <button onClick={() => setActiveTab('testimonials')} className={`flex items-center gap-4 px-6 py-4 text-left transition-all ${activeTab === 'testimonials' ? 'bg-slate-50 border-l-2 border-[#b48c36] text-[#b48c36]' : 'border-l-2 border-transparent text-slate-500 hover:text-slate-900 hover:bg-slate-50'}`}>
               <Award size={16} className="shrink-0" />
               <span className="font-serif text-lg font-bold">Testimonials</span>
            </button>

            <button onClick={() => setActiveTab('media')} className={`flex items-center gap-4 px-6 py-4 text-left transition-all ${activeTab === 'media' ? 'bg-slate-50 border-l-2 border-[#b48c36] text-[#b48c36]' : 'border-l-2 border-transparent text-slate-500 hover:text-slate-900 hover:bg-slate-50'}`}>
               <ImageIcon size={16} className="shrink-0" />
               <span className="font-serif text-lg font-bold">Media Library</span>
            </button>

            <button onClick={() => setActiveTab('maintenance')} className={`flex items-center gap-4 px-6 py-4 text-left transition-all ${activeTab === 'maintenance' ? 'bg-slate-50 border-l-2 border-rose-500 text-rose-500' : 'border-l-2 border-transparent text-slate-500 hover:text-rose-500 hover:bg-rose-50/30'}`}>
               <RefreshCw size={16} className="shrink-0" />
               <span className="font-serif text-lg font-bold">Maintenance</span>
            </button>
         </div>

         {/* Dashboard Main Content area - Pure White Cards on Slate-50 Canvas */}
         <div className="xl:col-span-9 animate-in fade-in duration-500 w-full overflow-hidden bg-white border border-slate-200 shadow-xl p-6 sm:p-10 min-h-[70vh]">
            
            {activeTab === 'overview' && <AdminOverview />}

            {activeTab === 'add-course' && (
               <>
                  <h2 className="text-2xl sm:text-3xl font-serif font-medium text-slate-950 border-b border-slate-100 pb-4 sm:pb-6 mb-6 sm:mb-8">
                     {editingCourseId ? "Edit Live Masterclass" : "Publish New Module"}
                  </h2>
                  
                  <form onSubmit={handleCourseSubmit} className="space-y-6 sm:space-y-8">
                     
                     {/* Native Image Upload Zone - Mobile Friendly */}
                     <div className="border border-slate-200 bg-slate-50 p-4 sm:p-6 flex flex-col sm:flex-row items-center gap-6 sm:gap-8 text-center sm:text-left">
                        <div className="w-full sm:w-48 h-40 sm:h-32 bg-slate-200 border border-slate-300 flex items-center justify-center shrink-0 relative overflow-hidden group">
                           {imagePreview ? (
                              <>
                                <img src={imagePreview} className="w-full h-full object-cover" alt="Preview"/>
                                <div className="absolute inset-0 bg-black/50 hidden group-hover:flex items-center justify-center transition-all">
                                   <span className="text-xs font-bold text-white uppercase tracking-widest">Change</span>
                                </div>
                              </>
                           ) : (
                              <div className="flex flex-col items-center text-slate-400 space-y-2">
                                 <ImagePlus size={24} />
                                 <span className="text-[9px] font-bold uppercase tracking-widest">No Cover Image</span>
                              </div>
                           )}
                           <input 
                             type="file" 
                             accept="image/*" 
                             onChange={handleImageChange}
                             className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" 
                           />
                        </div>
                        <div className="flex-1 w-full relative">
                           <h4 className="text-xs sm:text-sm font-bold text-slate-950 uppercase tracking-widest mb-1">Official Cover Thumbnail</h4>
                           <p className="text-xs sm:text-sm text-slate-500 mb-4 max-w-sm mx-auto sm:mx-0">Upload a high-fidelity image representing this module. It will be securely stored via Firebase.</p>
                           <div className="flex flex-wrap gap-3 items-center justify-center sm:justify-start">
                              <label className="text-[9px] sm:text-[10px] font-bold uppercase tracking-widest text-[#b48c36] border border-[#b48c36] px-4 py-3 sm:py-2 hover:bg-[#b48c36] hover:text-white transition-colors cursor-pointer inline-flex items-center justify-center gap-2">
                                 <UploadCloud size={14} /> 
                                 Select Local File
                                 <input type="file" accept="image/*" className="hidden" onChange={handleImageChange} />
                              </label>
                              <button 
                                type="button"
                                onClick={() => setIsMediaLibraryOpen(true)}
                                className="text-[9px] sm:text-[10px] font-bold uppercase tracking-widest text-slate-500 border border-slate-200 px-4 py-3 sm:py-2 hover:bg-slate-100 transition-colors inline-flex items-center justify-center gap-2"
                              >
                                 <ImageIcon size={14} /> 
                                 Choose from Lib
                              </button>
                           </div>
                        </div>

                        <Dialog open={isMediaLibraryOpen} onOpenChange={setIsMediaLibraryOpen}>
                          <DialogContent className="w-[95vw] max-w-5xl p-0 overflow-hidden border-none bg-white">
                            <DialogHeader className="p-5 sm:p-6 bg-background border-b border-border/30">
                              <DialogTitle>Select from Media Library</DialogTitle>
                            </DialogHeader>
                            <MediaLibrary
                              allowSelection
                              initialTab="images"
                              onSelect={(media) => {
                                setImagePreview(media.url)
                                setImageFile(null)
                                setIsMediaLibraryOpen(false)
                              }}
                            />
                          </DialogContent>
                        </Dialog>
                     </div>

                     <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                        <div className="space-y-2">
                           <label className="text-[9px] sm:text-[10px] font-bold text-slate-400 uppercase tracking-widest">Official Course Title</label>
                           <input type="text" required value={formData.title} onChange={e => setFormData({...formData, title: e.target.value})} className="w-full border border-slate-200 p-3 sm:p-4 text-sm focus:outline-none focus:border-[#b48c36] bg-slate-50 text-slate-950" placeholder="e.g. Indian Polity Deep Dive" />
                        </div>
                        <div className="space-y-2">
                           <label className="text-[9px] sm:text-[10px] font-bold text-slate-400 uppercase tracking-widest">Expert Faculty Name</label>
                           <input type="text" required value={formData.instructor} onChange={e => setFormData({...formData, instructor: e.target.value})} className="w-full border border-slate-200 p-3 sm:p-4 text-sm focus:outline-none focus:border-[#b48c36] bg-slate-50 text-slate-950" placeholder="e.g. Dr. K. N. Rao" />
                        </div>
                     </div>

                     <div className="space-y-2">
                        <label className="text-[9px] sm:text-[10px] font-bold text-slate-400 uppercase tracking-widest">Course Description / Overview</label>
                        <textarea rows={4} required value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})} className="w-full border border-slate-200 p-3 sm:p-4 text-sm focus:outline-none focus:border-[#b48c36] bg-slate-50 text-slate-950 resize-none" placeholder="Provide a detailed overview of the curriculum..."></textarea>
                     </div>

                     <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
                        <div className="space-y-2">
                           <label className="text-[9px] sm:text-[10px] font-bold text-slate-400 uppercase tracking-widest">Module Duration</label>
                           <input type="text" required value={formData.duration} onChange={e => setFormData({...formData, duration: e.target.value})} className="w-full border border-slate-200 p-3 sm:p-4 text-sm focus:outline-none focus:border-[#b48c36] bg-slate-50 text-slate-950" placeholder="e.g. 45 Days" />
                        </div>
                        <div className="space-y-2">
                           <label className="text-[9px] sm:text-[10px] font-bold text-slate-400 uppercase tracking-widest">Category Tag</label>
                           <input type="text" required value={formData.tag} onChange={e => setFormData({...formData, tag: e.target.value})} className="w-full border border-slate-200 p-3 sm:p-4 text-sm focus:outline-none focus:border-[#b48c36] bg-slate-50 text-slate-950" placeholder="e.g. High Yield" />
                        </div>
                        <div className="space-y-2">
                           <label className="text-[9px] sm:text-[10px] font-bold text-slate-400 uppercase tracking-widest">Exam Division</label>
                           <select value={formData.portal} onChange={e => setFormData({...formData, portal: e.target.value})} className="w-full border border-slate-200 p-3 sm:p-4 text-sm focus:outline-none focus:border-[#b48c36] bg-slate-50 text-slate-950">
                              <option value="tspsc">TSPSC Standard</option>
                              <option value="appsc">APPSC Standard</option>
                              <option value="all">All Divisions</option>
                           </select>
                        </div>
                     </div>

                     {/* Pricing Logic Container */}
                     <div className="p-4 sm:p-6 border border-slate-100 bg-slate-50 space-y-4 sm:space-y-6">
                        <div className="space-y-2">
                           <label className="text-[9px] sm:text-[10px] font-bold text-slate-500 uppercase tracking-widest">Access Protocol</label>
                           <div className="flex flex-row gap-2 sm:gap-4">
                              <button type="button" onClick={() => setFormData({...formData, type: "paid"})} className={`flex-1 flex items-center justify-center gap-1 sm:gap-2 p-3 sm:p-4 border text-[9px] sm:text-xs font-bold uppercase tracking-widest transition-all ${formData.type === 'paid' ? 'bg-[#b48c36]/10 border-[#b48c36] text-[#b48c36]' : 'bg-white border-slate-200 text-slate-500 hover:border-slate-300'}`}>
                                 Paid <CheckCircle2 size={12} className={formData.type === 'paid' ? 'opacity-100' : 'opacity-0'} />
                              </button>
                              <button type="button" onClick={() => setFormData({...formData, type: "free"})} className={`flex-1 flex items-center justify-center gap-1 sm:gap-2 p-3 sm:p-4 border text-[9px] sm:text-xs font-bold uppercase tracking-widest transition-all ${formData.type === 'free' ? 'bg-slate-950 border-slate-950 text-white' : 'bg-white border-slate-200 text-slate-500 hover:border-slate-300'}`}>
                                 Open <CheckCircle2 size={12} className={formData.type === 'free' ? 'opacity-100' : 'opacity-0'} />
                              </button>
                           </div>
                        </div>

                        {formData.type === "paid" && (
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 animate-in fade-in duration-300 pt-4 border-t border-slate-200">
                             <div className="space-y-2">
                                <label className="text-[9px] sm:text-[10px] font-bold text-slate-400 uppercase tracking-widest">Original Value (₹)</label>
                                <input type="number" required value={formData.originalPrice} onChange={e => setFormData({...formData, originalPrice: e.target.value})} className="w-full border border-slate-200 p-3 sm:p-4 text-sm focus:outline-none focus:border-[#b48c36] bg-white text-slate-950" placeholder="e.g. 5000" />
                             </div>
                             <div className="space-y-2">
                                <label className="text-[9px] sm:text-[10px] font-bold text-slate-400 uppercase tracking-widest">Discounted Value (₹)</label>
                                <input type="number" required value={formData.discountedPrice} onChange={e => setFormData({...formData, discountedPrice: e.target.value})} className="w-full border border-slate-200 p-3 sm:p-4 text-sm focus:outline-none focus:border-[#b48c36] bg-white text-slate-950" placeholder="e.g. 2499" />
                             </div>
                          </div>
                        )}
                     </div>

                     <div className="pt-2">
                        <button type="submit" disabled={isSubmitting} className="w-full bg-slate-950 text-white p-4 sm:p-5 text-[9px] sm:text-[10px] font-bold uppercase tracking-[0.2em] hover:bg-[#b48c36] transition-colors flex items-center justify-center gap-3 disabled:opacity-50 shadow-lg">
                           {isSubmitting ? <><Loader2 className="w-4 h-4 animate-spin" /> EXECUTING...</> : <><Database size={14} /> {editingCourseId ? "UPDATE LIVE MODULE RECORD" : "PUSH TO LIVE DATABASE"}</>}
                        </button>
                        {editingCourseId && (
                           <button type="button" onClick={() => { resetFormState(); setActiveTab("overview"); }} className="w-full mt-3 sm:mt-4 text-[9px] sm:text-[10px] text-slate-400 hover:text-slate-950 font-bold uppercase tracking-widest transition-colors py-2">
                              Cancel Edit Action
                           </button>
                        )}
                     </div>
                  </form>

                  {/* Course Registry / Manage Section */}
                  <div className="mt-12 pt-12 border-t border-slate-100">
                     <div className="flex items-center justify-between mb-8">
                        <div>
                           <h3 className="text-xl font-serif font-bold text-slate-950">Active Registry</h3>
                           <p className="text-[10px] text-slate-400 uppercase tracking-widest font-bold mt-1">Manage institutional assets appearing on platform</p>
                        </div>
                     </div>

                     {isLoadingCourses ? (
                        <div className="flex flex-col items-center justify-center py-12 text-[#b48c36]">
                           <Loader2 className="w-6 h-6 animate-spin mb-2" />
                           <p className="text-[8px] font-bold uppercase tracking-widest">Querying Registry...</p>
                        </div>
                     ) : coursesList.length === 0 ? (
                        <div className="bg-slate-50 border border-slate-100 p-8 text-center text-slate-400">
                           <p className="text-xs font-bold uppercase tracking-widest">Registry is currently empty</p>
                        </div>
                     ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left">
                           {coursesList.map((course) => (
                              <div key={course.id} className="bg-slate-50 border border-slate-200 p-4 flex items-center justify-between group hover:border-[#b48c36] transition-colors">
                                 <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 bg-slate-200 overflow-hidden shrink-0">
                                       {course.image && <img src={course.image} className="w-full h-full object-cover" alt="" />}
                                    </div>
                                    <div className="min-w-0">
                                       <h4 className="font-serif font-bold text-sm text-slate-950 line-clamp-1">{course.title}</h4>
                                       <div className="flex items-center gap-2 mt-0.5">
                                          <span className="text-[8px] font-bold text-slate-400 uppercase tracking-widest">{course.portal}</span>
                                          <div className={`w-1.5 h-1.5 rounded-full ${course.isVisible !== false ? 'bg-emerald-500' : 'bg-rose-500'}`} />
                                          <span className={`text-[8px] font-bold uppercase tracking-widest ${course.isVisible !== false ? 'text-emerald-500' : 'text-rose-500'}`}>
                                             {course.isVisible !== false ? 'Visible' : 'Hidden'}
                                          </span>
                                       </div>
                                    </div>
                                 </div>
                                 <div className="flex items-center gap-1">
                                    <button onClick={() => toggleCourseVisibility(course)} className={`p-2 transition-colors ${course.isVisible === false ? 'text-rose-500' : 'text-slate-400 hover:text-emerald-500'}`} title={course.isVisible === false ? "Show" : "Hide"}>
                                       {course.isVisible === false ? <EyeOff size={14} /> : <Eye size={14} />}
                                    </button>
                                    <button onClick={() => handleEditCourse(course)} className="p-2 text-slate-400 hover:text-[#b48c36] transition-colors" title="Edit">
                                       <Edit size={14} />
                                    </button>
                                    <button onClick={() => handleDeleteCourse(course.id)} className="p-2 text-slate-400 hover:text-red-500 transition-colors" title="Delete">
                                       <Trash2 size={14} />
                                    </button>
                                 </div>
                              </div>
                           ))}
                        </div>
                     )}
                  </div>
               </>
            )}

            {activeTab === 'faqs' && <FAQManager />}
            {activeTab === 'reviews' && <ReviewsManager />}
            {activeTab === 'quizzes' && <QuizzesManager />}
            {activeTab === 'pyqs' && <PYQManager />}
            {activeTab === 'articles' && <ArticlesManager />}
            {activeTab === 'magazines' && <MagazinesManager />}
            {activeTab === 'staff' && <StaffManager />}
            {activeTab === 'users' && <UsersManager />}
            {activeTab === 'testimonials' && <TestimonialsManager />}
            {activeTab === 'media' && <MediaLibrary />}
            {activeTab === 'maintenance' && <MaintenanceManager />}

         </div>
      </div>
    </div>
  )
}
